import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import {
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import LogsIcon from '@mui/icons-material/ArticleOutlined';
import DoneIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import SwapIcon from '@mui/icons-material/SwapHorizontalCircleOutlined';
import ChatIcon from '@mui/icons-material/CommentOutlined';
import TimeIcon from '@mui/icons-material/PendingActionsOutlined';
import AlertIcon from '@mui/icons-material/InfoOutlined';
import StartIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import AttachIcon from '@mui/icons-material/AttachFileOutlined';
import PlayIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import PauseIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LoopIcon from '@mui/icons-material/Loop';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SensorDoorIcon from '@mui/icons-material/SensorDoor';
import api from '../../../../services/api';
import FeedbackAlert, { FeedbackProps } from '../../../../components/FeedbackAlert';
import { ConfirmationDialog, ConfirmationDialogProps } from '../../../../components/ConfirmationDialog';

type DeviceType = {
  id: string;
  ip: string;
  name: string;
  session: string;
  action: string;
  parameters: string;
  in: string;
  out: string;
};

export default function ControlIDPage() {
  const theme = useTheme();

  const isDarkMode = theme.palette.mode === 'dark';
  const [devices, setDevices] = useState<DeviceType[]>([]);
  const [fetching, setFetching] = useState(false);
  const [submiting, setSubmiting] = useState(false);

  const [confirmation, setConfirmation] = useState<ConfirmationDialogProps>(null);

  const [feedback, setFeedback] = useState<FeedbackProps>({
    message: '',
    show: false,
  });

  const postCommand = async (id: string, par?: string) => {
    if (submiting) return;
    setSubmiting(true);
    try {
      const device = devices.find(f => f.id.toString() === id.toString());

      if (!device) {
        setFeedback({
          message: `Dispositivo não encontrado`,
          show: true,
          type: 'error',
        });
      }

      const response = await api.post(`vendors/control-id/execute_actions`, {
        ip: device.ip,
        session: device.session,
        action: device.action,
        parameters: par || device.parameters,
      });
      const status = response?.data?.actions?.[0]?.status || '';
      console.log(status);
      setFeedback({
        message: status === 'allowed' ? 'Liberado' : status === '' ? 'Liberado' : `Erro desconhecido: ${status}`,
        show: true,
        type: status === 'allowed' ? 'success' : status === '' ? 'success' : 'error',
      });
    } catch (error) {
      setFeedback({
        message: `Erro desconhecido: ${error}`,
        show: true,
        type: 'error',
      });
    }
    setConfirmation(old => ({ ...old, open: false }));
    setSubmiting(false);
  };

  const getDevices = async () => {
    if (fetching) return;
    setFetching(true);
    try {
      const response = await api.get(`vendors/control-id/devices`);
      setDevices(response.data);
    } catch (error) {
      setFeedback({
        message: `Erro desconhecido: ${error}`,
        show: true,
        type: 'error',
      });
    }
    setConfirmation(old => ({ ...old, open: false }));
    setFetching(false);
  };

  const renderFeedbackAlert = useMemo(
    () => (
      <FeedbackAlert
        message={feedback.message}
        show={feedback.show}
        durations={8000}
        type={feedback.type}
        onClose={() =>
          setFeedback(old => ({
            ...old,
            show: false,
          }))
        }
      />
    ),
    [feedback],
  );

  useEffect(() => {
    const fetchData = async () => {
      await getDevices();
    };

    fetchData().catch(console.error);
  }, []);
  if (fetching) {
    return (
      <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="caption" color="darkgray">
          Carregando...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Grid container flex={1} width="100%">
        {devices.map(device => {
          return (
            <Grid key={device.id} xs={12} sm={6} md={4} xl={3} p={1}>
              <Paper
                sx={{
                  display: 'flex',
                  height: '100%',
                  width: '100%',
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    flex: 1,
                    m: 2,
                    gap: 1,
                  }}
                >
                  <Typography>{device.name}</Typography>
                  {device.action === 'sec_box' && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => postCommand(device.id)}
                      startIcon={<SensorDoorIcon />}
                      color="success"
                    >
                      Liberar porta
                    </Button>
                  )}
                  {device.action === 'catra' && (
                    <>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => postCommand(device.id, device.parameters)}
                        startIcon={<LoopIcon />}
                        color="info"
                      >
                        Liberar ambos os lados
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => postCommand(device.id, device.in)}
                        startIcon={<RotateRightIcon />}
                        color="success"
                      >
                        Liberar Entrada
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => postCommand(device.id, device.out)}
                        startIcon={<RotateLeftIcon />}
                        color="warning"
                      >
                        Liberar Saída
                      </Button>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {renderFeedbackAlert}
      <ConfirmationDialog
        title={confirmation?.title ?? ''}
        content={confirmation?.content ?? ''}
        open={confirmation?.open ?? false}
        onClose={confirmation?.onClose ?? null}
        onClickNo={confirmation?.onClickNo ?? null}
        onClickYes={confirmation?.onClickYes ?? null}
        loading={submiting}
      />
    </>
  );
}
